import React, {useEffect, useState} from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Divider,
  TableContainer,
  ModalFooter,
  useDisclosure,
  Checkbox,
  Tfoot,
  Icon,
  Center,
  Input,
  Text,
  Spinner,
  Button,
  Stack,
  Flex,
} from '@chakra-ui/react';
import {CustomCheckbox, CustomInput, DatePicker} from '../../components/General/FormInputs';
import BoxCard from '../../components/General/BoxCard';
import {useTitle} from '../../components/General/Layout';
import {FaTrashAlt, FaPlusSquare, FaChevronUp, FaChevronDown} from 'react-icons/fa';
import {get, post, postDelete} from '../../service/ApiService';
import {Location, Group, Client as ClientUrls} from '../../service/Urls';
import {isAdmin} from '../../helpers/localStorage';
import ErrorHelper from '../../service/ErrorHelper';
import {useQueryClient, useQuery} from 'react-query';
import {debounce} from 'lodash';
import Pagination from '../../components/General/Pagination';
import {useForm, useFieldArray} from 'react-hook-form';
import ClientSelectInput from '../../components/Client/ClientSelectInput';
import ServiceTypes from '../../constants/ServiceTypes';
import LocationRegionSelect from '../../components/Region/LocationRegionSelect';
import GroupSelectNew from '../../components/Group/GroupSelectNew';
import SurveyTableModal from '../../components/Surveys/SurveyTableModal';

const PAGE_SIZE = 8;
const DEBOUNCE_TIME = 600;

export default function LocationMaintenance() {
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(sessionStorage.getItem('location-selectedLocationId') ?? null);
  const [toastResponse, setToastResponse] = useState(undefined);
  const [isSalesMan, setIsSalesMan] = useState(true);
  const [_, setTitle] = useTitle('Location Maintenance');

  const [page, setPage] = useState(parseInt(sessionStorage.getItem('location-page') ?? '1'));

  // Search State
  const [clientId, setClientId] = useState(sessionStorage.getItem('location-clientId') ?? '');
  const [clientName, setClientName] = useState(sessionStorage.getItem('location-clientName') ?? '');
  const [loId, setLoId] = useState(sessionStorage.getItem('location-loId') ?? '');
  const [loName, setLoName] = useState(sessionStorage.getItem('location-loName') ?? '');
  const [loCity, setLoCity] = useState(sessionStorage.getItem('location-loCity') ?? '');
  const [loState, setLoState] = useState(sessionStorage.getItem('location-loState') ?? '');
  const [loRptname, setLoRptname] = useState(sessionStorage.getItem('location-loRptname') ?? '');

  // Order State
  const [order, setOrder] = useState('');
  const [isDesc, setIsDesc] = useState(false);

  const queryClient = useQueryClient();

  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isSurveysOpen,
    onOpen: onSurveysOpen,
    onClose: onSurveysClose,
  } = useDisclosure({defaultIsOpen: !!sessionStorage.getItem('location-survey-open')});

  const {data: clientList} = useQuery(['client', 'clientList'], () => get(ClientUrls.LIST + '?PageSize=1000'));

  const {data: locations, isFetching} = useQuery(
    ['locations', page, clientId, clientName, loId, loName, loCity, loState, loRptname, order, isDesc],
    async () => {
      return get(
        `${Location.FULL_LIST}?page=${page}&pagesize=${PAGE_SIZE}&clId=${clientId}&clientName=${clientName}&loId=${loId}&loName=${loName}&loCity=${loCity}&loState=${loState}&loRptname=${loRptname}&order=${order}&isDesc=${isDesc}`
      );
    },
    {
      onError: () => {
        setToastResponse({error: 'Error loading locations'});
      },
      keepPreviousData: true,
    }
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: {errors},
  } = useForm();

  const {
    fields: aliases,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    name: 'aliases',
    control,
  });

  const currentLocationId = watch('id');
  const currentClId = watch('loClient');

  const {data: groups} = useQuery(
    ['groups', currentClId],
    async () => {
      return get(`${Group.LIST_BY_CLIENT}?clientId=${currentClId}`);
    },
    {
      enabled: currentClId != null,
    }
  );

  const handleActionClick = (index: number) => {
    const location = locations?.data[index];
    reset({...location, loSince: location?.loSince?.substring(0, location.loSince.indexOf('T')) ?? ''});
    onOpen();
  };

  const handleAddLocation = () => {
    reset({
      id: null,
      clientId: '',
      regionId: '',
      loId: '',
      loName: '',
      loClient: '',
      loAdd1: '',
      loAdd2: '',
      loZip: '',
      loCity: '',
      loState: '',
      loContact: '',
      loPhone: '',
      loFax: '',
      loSince: '',
      loRegion: '',
      loGroup: '',
      loSscode: '',
      loRptname: '',
      loFuneral: false,
      loCremate: false,
      loCemeter: false,
      loMonu: false,
      loPre: false,
      loPetcrem: false,
      loEvent: false,
      loInact: false,
      aliases: [],
    });
    onOpen();
  };

  const handleViewSurveys = (locationId: string) => {
    setSelectedLocationId(locationId);
    onSurveysOpen();
  };

  const onSaveLocation = async location => {
    const id = location.id;

    if (id === null) {
      addLocation(location);
    } else {
      updateLocation(location);
    }
  };

  const addLocation = async location => {
    const lo = {
      clientId: location.clientId,
      regionId: location.regionId,
      loName: location.loName,
      loClient: location.loClient,
      loAdd1: location.loAdd1,
      loAdd2: location.loAdd2,
      loZip: location.loZip,
      loCity: location.loCity,
      loState: location.loState,
      loContact: location.loContact,
      loPhone: location.loPhone,
      loFax: location.loFax,
      loSince: location.loSince,
      loRegion: location.loRegion,
      loGroup: location.loGroup,
      loSscode: location.loSscode,
      loRptname: location.loRptname,
      loFuneral: location.loFuneral,
      loCremate: location.loCremate,
      loCemeter: location.loCemeter,
      loMonu: location.loMonu,
      loPre: location.loPre,
      loPetcrem: location.loPetcrem,
      loEvent: location.loEvent,
      loInact: location.loInact,
      aliases: location.aliases,
    };
    const response = await post(Location.ADD, lo);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    queryClient.invalidateQueries('locations');
    setToastResponse({response: response, successMessage: 'Successfully added ' + lo.loName});
    onClose();
    setPage(1);
  };

  const updateLocation = async location => {
    const response = await post(Location.UPDATE + '/' + location?.id, location);

    if (!response.isSuccess) {
      setToastResponse({response: response, errorMessage: response.error});
      return;
    }

    queryClient.invalidateQueries('locations');
    setToastResponse({response: response, successMessage: 'Successfully updated ' + location?.loName});
    onClose();
  };

  const deleteLocation = async (index: number) => {
    const location = locations?.data[index];
    if (confirm('Are you sure you want to remove ' + location?.loName + '?')) {
      const response = await postDelete(Location.DELETE + '/' + location?.id);

      if (!response.isSuccess) {
        setToastResponse({response: response, errorMessage: response.error});
        return;
      }

      queryClient.invalidateQueries('locations');
      setToastResponse({response: response, successMessage: 'Successfully deleted ' + location?.loName});
    }
  };

  useEffect(() => {
    if (isAdmin()) setIsSalesMan(false);

    setTitle('Location Maintenance');
  }, []);

  useEffect(() => {
    sessionStorage.setItem('location-page', page.toString());
  }, [page]);

  useEffect(() => {
    sessionStorage.setItem('location-survey-open', isSurveysOpen ? 'true' : '');
  }, [isSurveysOpen]);

  useEffect(() => {
    sessionStorage.setItem('location-selectedLocationId', selectedLocationId ?? '');
  }, [selectedLocationId]);

  const TableHeading = ({label, value}: {label: string; value: string}) => {
    const isOrder = order === value;

    const handleHeadingClick = () => {
      if (order === value && !isDesc) {
        setIsDesc(true);
        return;
      }

      if (order === value && isDesc) {
        setOrder('');
        setIsDesc(false);
        return;
      }

      setOrder(value);
      setIsDesc(false);
    };

    return (
      <Flex gap={3} cursor="pointer" onClick={handleHeadingClick}>
        <Text>{label}</Text>
        <Icon
          as={isOrder && isDesc ? FaChevronDown : FaChevronUp}
          opacity={isOrder ? 1 : 0.3}
          color={isOrder ? 'tertiary.600' : undefined}
          boxSize="15px"
        />
      </Flex>
    );
  };

  return (
    <Box mr="2">
      <BoxCard>
        <TableContainer position="relative" minH="200px">
          <Center
            position="absolute"
            left="50%"
            top="45%"
            bgColor="whiteAlpha.900"
            padding={3}
            borderRadius={8}
            opacity={isFetching ? 1 : 0}
            visibility={isFetching ? 'visible' : 'hidden'}
            transition="opacity 500ms ease-in"
            transitionDelay="150ms"
          >
            <Spinner speed="700ms" />
          </Center>
          <Table border="solid 2px #0c5f78" variant="striped" colorScheme="mint">
            <Thead>
              <Tr border="solid 2px #0c5f78">
                <Th />
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={clientId}
                    onChange={debounce(event => {
                      setPage(1);
                      setClientId(event.target.value);
                      sessionStorage.setItem('location-clientId', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={clientName}
                    onChange={debounce(event => {
                      setPage(1);
                      setClientName(event.target.value);
                      sessionStorage.setItem('location-clientName', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={loId}
                    onChange={debounce(event => {
                      setPage(1);
                      setLoId(event.target.value);
                      sessionStorage.setItem('location-loId', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={loName}
                    onChange={debounce(event => {
                      setPage(1);
                      setLoName(event.target.value);
                      sessionStorage.setItem('location-loName', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={loCity}
                    onChange={debounce(event => {
                      setPage(1);
                      setLoCity(event.target.value);
                      sessionStorage.setItem('location-loCity', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={loState}
                    onChange={debounce(event => {
                      setPage(1);
                      setLoState(event.target.value);
                      sessionStorage.setItem('location-loState', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
                <Th>
                  <Input
                    borderColor="gray.300"
                    defaultValue={loRptname}
                    onChange={debounce(event => {
                      setPage(1);
                      setLoRptname(event.target.value);
                      sessionStorage.setItem('location-loRptname', event.target.value);
                    }, DEBOUNCE_TIME)}
                  />
                </Th>
              </Tr>
              <Tr border="solid 2px #0c5f78">
                <Td>
                  {!isSalesMan ? (
                    <Button
                      colorScheme="tertiary"
                      title="Add"
                      size="md"
                      leftIcon={<FaPlusSquare />}
                      onClick={() => handleAddLocation()}
                      aria-label="Add Location"
                    >
                      Add Location
                    </Button>
                  ) : null}
                </Td>
                <Th>
                  <TableHeading label="Client Id" value="ClientId" />
                </Th>
                <Th>
                  <TableHeading label="Client Name" value="ClientName" />
                </Th>
                <Th>
                  <TableHeading label="Loc Id" value="LoId" />
                </Th>
                <Th>
                  <TableHeading label="Location Name" value="LoName" />
                </Th>
                <Th>
                  <TableHeading label="City" value="LoCity" />
                </Th>
                <Th>
                  <TableHeading label="State" value="LoState" />
                </Th>
                <Th>
                  <TableHeading label="Report Name" value="LoRptname" />
                </Th>
                <Th>
                  <Text>Inactive</Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {locations?.data?.map((location, index) => {
                const matchingClient = clientList?.data?.find((client: any) => client.id === location.clientId);
                return (
                  <Tr key={location.id}>
                    <Td>
                      {!isSalesMan && (
                        <IconButton
                          onClick={() => deleteLocation(index)}
                          colorScheme="red"
                          title="Delete"
                          size="md"
                          icon={<FaTrashAlt />}
                          aria-label="Delete"
                          mr={3}
                        />
                      )}
                      <Button colorScheme="secondary" onClick={() => handleViewSurveys(location.id)} mr={3}>
                        Surveys
                      </Button>
                      <Button colorScheme="tertiary" onClick={() => handleActionClick(index)} mr={3}>
                        {isSalesMan ? 'View' : 'Edit'}
                      </Button>
                    </Td>
                    <Td>{matchingClient?.clId ?? location.clientId}</Td>
                    <Td>{location.clientName}</Td>
                    <Td>{location.loId}</Td>
                    <Td>{location.loName}</Td>
                    <Td>{location.loCity}</Td>
                    <Td>{location.loState}</Td>
                    <Td>{location.loRptname}</Td>
                    <Td>
                      <Checkbox colorScheme="tertiary" isChecked={location.loInact} isReadOnly />
                    </Td>
                  </Tr>
                );
              })}
              {Array(PAGE_SIZE - (locations?.data?.length ?? 0))
                .fill(0)
                .map((_, index) => (
                  <Tr key={index}>
                    <Td>&nbsp;</Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                    <Td></Td>
                  </Tr>
                ))}
            </Tbody>
            <Tfoot></Tfoot>
          </Table>
          <Pagination page={page - 1} pageCount={locations?.pageCount ?? 0} onPageChange={({selected}) => setPage(selected + 1)} />
        </TableContainer>
      </BoxCard>
      <Modal blockScrollOnMount={false} size={'6xl'} isOpen={isOpen} onClose={onClose}>
        <form onSubmit={handleSubmit(onSaveLocation)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader id="header">
              <Flex>
                {isSalesMan ? 'View Location' : 'Edit Location'}
                {!isSalesMan && (
                  <Button colorScheme="tertiary" ml="5" type="submit">
                    Save
                  </Button>
                )}
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Stack>
                <CustomInput id="id" register={register} type="hidden" />

                <Box bgGradient={'linear(to-tr, blue.600, blue.200)'} boxShadow="xs" rounded="lg" border="1px" p="2" px="30" mb="2">
                  <Text fontWeight="bold" fontSize="xl" mb={3}>
                    Survey Types
                  </Text>
                  <Flex justify="space-evenly" mb={3}>
                    <CustomCheckbox id="loFuneral" name="Funeral" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loCremate" name="Cremation" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loCemeter" name="Cemetery" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loMonu" name="Monument" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loPre" name="PreNeed" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loPetcrem" name="Pet Cremation" control={control} isReadOnly={isSalesMan} w="100%" />
                    <CustomCheckbox id="loEvent" name="Event Planning" control={control} isReadOnly={isSalesMan} w="100%" />
                  </Flex>
                </Box>

                <Flex gap={3} alignItems="center">
                  {currentLocationId != null && <CustomInput id="loId" register={register} name="Location Id" isReadOnly />}
                  <CustomInput id="loName" register={register} name="Location Name" errors={errors} required isReadOnly={isSalesMan} flex={1} />
                  <CustomCheckbox id="loInact" control={control} name="Inactive" isReadOnly={isSalesMan} w="auto" />
                </Flex>

                <ClientSelectInput setClId={val => setValue('loClient', val)} control={control} name="clientId" required isReadOnly={isSalesMan} />

                <CustomInput id="loAdd1" register={register} name="Address 1" errors={errors} isReadOnly={isSalesMan} />
                <CustomInput id="loAdd2" register={register} name="Address 2" isReadOnly={isSalesMan} />

                <Flex gap={3}>
                  <CustomInput id="loZip" register={register} name="Zip" errors={errors} required isReadOnly={isSalesMan} />
                  <CustomInput id="loCity" register={register} name="City" errors={errors} isReadOnly={isSalesMan} />
                  <CustomInput id="loState" register={register} name="State" errors={errors} isReadOnly={isSalesMan} />
                </Flex>

                <Flex gap={3}>
                  <CustomInput id="loContact" register={register} name="Contact" errors={errors} isReadOnly={isSalesMan} />
                  <CustomInput id="loPhone" register={register} name="Phone" errors={errors} isReadOnly={isSalesMan} />
                  <CustomInput id="loFax" register={register} name="Fax" errors={errors} isReadOnly={isSalesMan} />
                  <DatePicker id="loSince" register={register} name="Location Since" isReadOnly={isSalesMan} errors={errors} required />
                </Flex>

                <Flex gap={3}>
                  <Box flex={1}>
                    <LocationRegionSelect
                      isReadOnly={isSalesMan}
                      name="regionId"
                      setCode={(code: number) => setValue('loRegion', code)}
                      control={control}
                      required
                    />
                  </Box>
                  <Box flex={1}>
                    <GroupSelectNew groups={groups?.data ?? []} isReadOnly={isSalesMan} name="loGroup" control={control} required />
                  </Box>
                </Flex>

                <Flex gap={3}>
                  <CustomInput w="30%" register={register} id="loSscode" name="Spreadsheet Code" errors={errors} isReadOnly={isSalesMan} />
                  <CustomInput
                    w="30%"
                    register={register}
                    id="loRptname"
                    name="Name For Reports"
                    errors={errors}
                    isReadOnly={isSalesMan}
                    maxLength={40}
                  />
                </Flex>
              </Stack>

              <Divider mt="5" colorScheme={'mint'} width={{base: '100%'}} />
              <TableContainer width={{base: '100%'}}>
                <Table variant="striped" border="solid 2px #0c5f78" colorScheme="mint" size={'md'}>
                  <Thead>
                    <Tr border="solid 2px #0c5f78">
                      <Th>Alias Number</Th>
                      <Th>Alias</Th>
                      <Td>
                        {!isSalesMan && (
                          <Button
                            colorScheme="tertiary"
                            title="Add"
                            size="md"
                            leftIcon={<FaPlusSquare />}
                            onClick={() => appendAlias({})}
                            aria-label="Add Alias"
                          >
                            Add Alias
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {aliases.map((alias, index) => {
                      return (
                        <Tr key={alias.id}>
                          <Td>{index + 1}</Td>
                          <Td>
                            <Box padding={2}>
                              {isSalesMan ? (
                                alias.calias
                              ) : (
                                <CustomInput id={`aliases.${index}.calias`} register={register} errors={errors} required />
                              )}
                            </Box>
                          </Td>
                          <Td>
                            {!isSalesMan && (
                              <IconButton
                                colorScheme="tertiary"
                                title="Delete"
                                size="md"
                                icon={<FaTrashAlt />}
                                onClick={() => removeAlias(index)}
                                aria-label="Delete Alias"
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </ModalBody>

            <ModalFooter></ModalFooter>
          </ModalContent>
        </form>
      </Modal>

      <SurveyTableModal filterName="locationId" filterId={selectedLocationId} isOpen={isSurveysOpen} onClose={onSurveysClose} />

      <ErrorHelper toastResponse={toastResponse} />
    </Box>
  );
}
